// Private & group workshop offerings. Cards on the homepage "Workshops" section
// link through to /workshops/:slug for the full description + pricing.
export const offerings = [
  {
    slug: 'urban-foraging',
    title: 'Urban Foraging',
    duration: '2.5 hours',
    img: '/backgrounds/urban-foraging.jpg',
    desc: 'A reframing of the everyday landscape. This workshop invites you to see familiar streets, parks, and overlooked corners differently – as places of resilience, nourishment, and quiet abundance. Perfect for beginners and the quietly curious.',
    includesSummary: ['Guided urban foraging walk', 'Recipes to take home', 'Grounding meditation'],
    bring: 'Comfortable walking shoes, a small bag or basket, water bottle.',
    pricing: { min: '$300', extra: '$30', max: 20 },
  },
  {
    slug: 'coastal-foraging',
    title: 'Coastal Forage & Feast',
    duration: '2.5hrs · or 5hrs with cook-up',
    img: '/backgrounds/coral-reef-underwater.jpg',
    desc: "A guided walk along the shoreline exploring edible and useful seaweeds and coastal species, shaped by tide, season, and respect for Country. Where possible, we'll prepare and share a simple meal by the sea.",
    includesSummary: ['Guided coastal foraging walk', 'Shared foraged meal by the sea (5hr option)', 'Recipes to take home', 'Grounding meditation'],
    bring: "Shoes you don't mind getting wet, layers, a small bag or basket, water bottle.",
    pricing: { min: '$450', minNote: '/ $900 with meal', extra: '$45', extraNote: '/ $150 with meal', max: 20 },
  },
  {
    slug: 'mushroom-foraging',
    title: 'Mushroom Foraging',
    duration: '4 hours',
    img: '/backgrounds/saffies.jpeg',
    desc: 'A seasonal guided forage covering safe identification, ecology and preparation of wild fungi. Available late autumn and early winter.',
    includesSummary: ['Guided forage through pine forest', 'Shared foraged meal', 'Recipes to take home', 'Grounding meditation'],
    bring: 'Comfortable shoes, weather-appropriate layers, a small basket or bag, water bottle.',
    pricing: { min: '$630', extra: '$90', max: 15 },
  },
  {
    slug: 'sea-urchin-forage-feast',
    title: 'Sea Urchin Forage & Feast',
    duration: '4 hours',
    img: '/backgrounds/sea-urchin-forage.jpg',
    desc: 'Learn why locally invasive Long-spined Sea Urchins threaten our southern kelp forests – making them a sustainable source of nutrition. We teach how to forage, process and cook them. Each participant crafts their own foraging tool to take home.',
    pricing: { min: '$475', extra: '$95', max: 6 },
  },
  {
    slug: 'plant-printing-alchemy-of-steam-and-pigment',
    title: 'Plant Printing on Natural Fabric: The Alchemy of Steam & Pigment',
    duration: '6 hours',
    img: '/backgrounds/ecoprint-hero.png',
    heroImg: '/backgrounds/ecoprint.jpg',
    images: ['/backgrounds/ecoprint2.png'],
    desc: 'Using natural pigments, tannins, and textures found in leaves, flowers, and bark, we create one-of-a-kind prints directly onto natural fabrics – an ancient-meets-modern textile art. A hands-on textile masterclass, no experience necessary.',
    includesSummary: ['All fabrics, mordants & plant specimens', 'Your finished eco-printed pieces', 'Light refreshments', 'Printed plant guide & reference card'],
    bring: "All materials are provided. Optionally, bring a fabric or textile item from home you'd like to print on, a plant or leaves you're curious to work with, and lunch for the day.",
    pricing: { min: '$1,000', extra: '$250', max: 15 },
  },
  {
    slug: 'plant-printing-botanical-impressions',
    title: 'Plant Printing: Botanical Impressions',
    duration: '3 hours',
    img: '/backgrounds/botanical-impressions.jpg',
    desc: "Plant printing, or stamping, offers a slow, intentional practice that invites the natural world to be a direct creative collaborator. By gathering foliage to leave its tactile memory on fabric, this sensory process transforms textiles into living art that honors the delicate intelligence of the seasons. Bring something you'd like to upcycle, or use our textiles provided.",
    pricing: { min: '$450', extra: '$75', max: 15 },
  },
  {
    slug: 'weaving-with-local-fibres',
    title: 'Weaving with Local Fibres',
    duration: '2.5 hours',
    img: '/backgrounds/weaving-local-fibres.png',
    desc: "In this hands-on gathering, we honor Lomandra longifolia - one of Australia's most versatile and durable native plants. You will learn the foundational practices of cold-processing, splitting, and prepping the raw blades, before diving into traditional weaving techniques. Leave with a woven object and a deep, somatic appreciation for the structural intelligence of the land.",
    pricing: { min: '$400', extra: '$50', max: 20 },
  },
  {
    slug: 'australian-incense-crafting',
    title: 'Australian Incense Crafting',
    duration: '2.5 hours',
    img: '/backgrounds/incense-crafting.jpg',
    desc: 'In this hands-on workshop, we slow down to connect with the raw, therapeutic profiles of native Australian botanicals. You will learn the ancient art of incense crafting – pulverising creamy sandalwood, crisp eucalyptus, and bright lemon myrtle, before binding them with natural acacia gum. You will knead and shape your own custom-blended incense cones to take home for your daily rituals. The specific leaves, flowers, bark and resins used vary between workshops depending on seasonal availability.',
    pricing: { min: '$900', extra: '$150', max: 20 },
  },
  {
    slug: 'botanical-skincare',
    title: 'Handcrafted Botanical Skincare',
    duration: '2.5 hours',
    img: '/backgrounds/botanical-skincare.png',
    desc: 'A slow, hands-on experience crafting natural skincare products from scratch, made with locally foraged plants and locally harvested beeswax. Learn the plant medicine behind every ingredient you use.',
    includesSummary: ['All materials & locally sourced ingredients', 'Expert guidance on plant medicine', 'Finished herbal balm to take home'],
    bring: 'Just yourself – all materials are provided.',
    pricing: { min: '$600', extra: '$150', max: 20 },
  },
  {
    slug: 'botanical-skincare-for-dogs',
    title: 'Handcrafted Botanical Skincare for Dogs',
    duration: '2.5 hours',
    img: '/backgrounds/pet-herbal-balm-july25.png',
    heroPosition: 'top',
    heroHeight: '600px',
    desc: 'A hands-on workshop in plant-based animal care. You\'ll make a balm for your dog from scratch, learning the plants, the process, and the reasoning behind it, and leave with a finished tin ready to use on dry paws, rough noses, and small skin irritations. We begin with the plants themselves: calendula for its gentle anti-inflammatory action, beeswax for protection and structure, shea and jojoba to soften and restore. The specific plants used vary between workshops depending on seasonal availability.',
    includesSummary: ['Hands-on balm making from scratch', 'Finished herbal balm tin to take home', 'Recipes to recreate at home', 'All materials & tea'],
    bring: 'All materials and equipment are provided. No experience needed. Note: dogs cannot attend due to hot oils and waxes. The balm made in this workshop is a cosmetic product for external use on dogs – it is not a veterinary treatment.',
    pricing: { min: '$600', extra: '$150', max: 20 },
  },
  {
    slug: 'guided-bushwalk-and-yoga',
    title: 'Guided Bushwalk, Forest Bathing & Yoga',
    duration: 'Flexible',
    img: '/backgrounds/guided-bushwalk-yoga.jpg',
    desc: 'A slow, sensory bushwalk inspired by forest bathing (Shinrin-yoku), paired with an outdoor yoga session (40–60 minutes). We pick the route and pace to suit your group, weaving mindful immersion in nature with movement and stillness through the landscape.',
    pricing: { min: '$540', extra: '$90', max: 20 },
  },
  {
    slug: 'nature-connection-sensory-play',
    title: 'Nature Connection & Sensory Play',
    duration: '2.5 hours',
    img: '/backgrounds/nature-connection-sensory-play-new.jpg',
    desc: 'A playful, grounding experience designed to reconnect people with their senses, the landscape, and each other. Through gentle games, movement, and sensory invitations, we explore how to listen more closely to the natural world and to ourselves.',
    includesSummary: ['Guided sensory activities', 'Nature-based games & movement', 'Grounding meditation'],
    bring: 'Comfortable clothes you can move in, water bottle, willingness to play.',
    pricing: { min: '$320', extra: '$40', max: 20 },
  },
  {
    slug: 'deep-ecology-meditation',
    title: 'Deep Ecology Meditation',
    duration: '1.5 hours',
    img: '/backgrounds/deep-ecology-meditation.jpg',
    desc: 'A guided meditation drawing on deep ecology philosophy. Practices for coming back into relationship with the more-than-human world. Gentle, grounding and necessary.',
    pricing: { min: '$200', extra: '$20', max: 30 },
  },
];
